import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "./api";

const AuthContext = createContext(null);

/* ---------- role helpers -------------------------------------------------- */
export const ROLE_SUPER_ADMIN = "super_admin";
export const ROLE_ADMIN       = "admin";
export const ROLE_CLIENT      = "client";

/** Legacy backend value `user` is treated as `client` throughout the UI. */
export const normalizeRole = (r) => (r === "user" || r == null ? ROLE_CLIENT : r);
export const isAdminRole   = (r) => [ROLE_ADMIN, ROLE_SUPER_ADMIN].includes(normalizeRole(r));
export const isSuperAdmin  = (r) => normalizeRole(r) === ROLE_SUPER_ADMIN;

/** Permission check. Super-admin (`*`) always wins. */
export const hasPermission = (user, key) => {
  if (!user) return false;
  const perms = user.permissions || [];
  if (perms.includes("*")) return true;
  return perms.includes(key);
};

/* -------------------------------------------------------------------------- */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const _doLogin = async (endpoint, email, password) => {
    try {
      const { data } = await api.post(endpoint, { email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const login       = (email, password) => _doLogin("/auth/login",        email, password);
  const adminLogin  = (email, password) => _doLogin("/auth/admin-login",  email, password);
  const superLogin  = (email, password) => _doLogin("/auth/super-login",  email, password);

  const register = async (name, email, password) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const forceChangePassword = async (newPassword) => {
    try {
      const { data } = await api.post("/auth/force-change-password", { new_password: newPassword });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, adminLogin, superLogin, register, logout, refresh, setUser,
      forceChangePassword,
      // helpers surfaced for convenience
      isAdmin: isAdminRole(user?.role),
      isSuper: isSuperAdmin(user?.role),
      hasPermission: (key) => hasPermission(user, key),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
